/**
 * The biomarkers Lattice knows, each with a stable code and the canonical unit every value is
 * converted to before the formula engine sees it. Codes are ours and never change. LOINC codes
 * are attached where one clearly applies, for imports and for anyone checking the mapping.
 *
 * How printed names and units map onto these lives in `packages/ingest`. This file only says what
 * exists.
 */

export interface Biomarker {
  readonly code: string;
  readonly name: string;
  /** The unit values are normalized to. */
  readonly unit: string;
  readonly loinc: string | null;
}

export const BIOMARKERS = [
  { code: "vitamin_d_25oh", name: "Vitamin D, 25-hydroxy", unit: "ng/mL", loinc: "1989-3" },
  { code: "vitamin_b12", name: "Vitamin B12", unit: "pg/mL", loinc: "2132-9" },
  { code: "folate_serum", name: "Folate, serum", unit: "ng/mL", loinc: "2284-8" },
  { code: "ferritin", name: "Ferritin", unit: "ng/mL", loinc: "2276-4" },
  { code: "magnesium_rbc", name: "Magnesium, RBC", unit: "mg/dL", loinc: "2594-0" },
  { code: "magnesium_serum", name: "Magnesium, serum", unit: "mg/dL", loinc: "19123-9" },
  { code: "zinc", name: "Zinc", unit: "µg/dL", loinc: "5763-8" },
  { code: "omega3_index", name: "Omega-3 index", unit: "%", loinc: null },
  { code: "homocysteine", name: "Homocysteine", unit: "µmol/L", loinc: "13965-9" },
  { code: "hs_crp", name: "hs-CRP", unit: "mg/L", loinc: "30522-7" },
  { code: "hba1c", name: "Hemoglobin A1c", unit: "%", loinc: "4548-4" },
  { code: "glucose", name: "Glucose", unit: "mg/dL", loinc: "2345-7" },
  { code: "insulin_fasting", name: "Insulin, fasting", unit: "µIU/mL", loinc: "20448-7" },
  { code: "cholesterol_total", name: "Cholesterol, total", unit: "mg/dL", loinc: "2093-3" },
  { code: "triglycerides", name: "Triglycerides", unit: "mg/dL", loinc: "2571-8" },
  { code: "hdl", name: "HDL cholesterol", unit: "mg/dL", loinc: "2085-9" },
  { code: "ldl", name: "LDL cholesterol", unit: "mg/dL", loinc: "13457-7" },
  { code: "apob", name: "Apolipoprotein B", unit: "mg/dL", loinc: "1884-6" },
  { code: "wbc", name: "White blood cells", unit: "x10^3/µL", loinc: "6690-2" },
  { code: "rbc", name: "Red blood cells", unit: "x10^6/µL", loinc: "789-8" },
  { code: "hemoglobin", name: "Hemoglobin", unit: "g/dL", loinc: "718-7" },
  { code: "hematocrit", name: "Hematocrit", unit: "%", loinc: "4544-3" },
  { code: "mcv", name: "MCV", unit: "fL", loinc: "787-2" },
  { code: "platelets", name: "Platelets", unit: "x10^3/µL", loinc: "777-3" },
  { code: "bun", name: "BUN", unit: "mg/dL", loinc: "3094-0" },
  { code: "creatinine", name: "Creatinine", unit: "mg/dL", loinc: "2160-0" },
  { code: "egfr", name: "eGFR", unit: "mL/min/1.73m2", loinc: "98979-8" },
  { code: "sodium", name: "Sodium", unit: "mmol/L", loinc: "2951-2" },
  { code: "potassium", name: "Potassium", unit: "mmol/L", loinc: "2823-3" },
  { code: "calcium", name: "Calcium", unit: "mg/dL", loinc: "17861-6" },
  { code: "albumin", name: "Albumin", unit: "g/dL", loinc: "1751-7" },
  { code: "total_protein", name: "Total protein", unit: "g/dL", loinc: "2885-2" },
  { code: "alt", name: "ALT", unit: "U/L", loinc: "1742-6" },
  { code: "ast", name: "AST", unit: "U/L", loinc: "1920-8" },
  { code: "creatine_kinase", name: "Creatine kinase", unit: "U/L", loinc: "2157-6" },
  { code: "tsh", name: "TSH", unit: "µIU/mL", loinc: "3016-3" },
  { code: "free_t4", name: "Free T4", unit: "ng/dL", loinc: "3024-7" },
  { code: "testosterone_total", name: "Testosterone, total", unit: "ng/dL", loinc: "2986-8" },
] as const satisfies readonly Biomarker[];

export type BiomarkerCode = (typeof BIOMARKERS)[number]["code"];

const BY_CODE = new Map<string, Biomarker>(BIOMARKERS.map((b) => [b.code, b]));

export function biomarker(code: BiomarkerCode): Biomarker {
  const found = BY_CODE.get(code);

  if (!found) throw new Error(`unknown biomarker ${code}`);

  return found;
}

export function isBiomarkerCode(code: string): code is BiomarkerCode {
  return BY_CODE.has(code);
}
